import styled from 'styled-components';
import { useMemo } from 'react';
import { academyCustomCSS } from '~/constants/academyCustomCSS';
import { media, theme } from '../../../../styles';
import academyStyles from '../academy-styles';
import SubMenuLink from './sub-menu-link';

export default function AcademyThemeSubMenu(props) {
  const {
    adminUser,
    certificationsEnabled,
    currentMenu,
    currentUser,
    customPages,
    eventsEnabled,
    myTrainingsEnabled,
    pathsEnabled,
    subnavActiveLinkColor,
    subnavAlign,
    subnavBackgroundColor,
    subnavLinkColor,
    themeOptions,
    collectionsEnabled,
    pagesOrder,
  } = props;

  const { ACADEMY_SUB_MENU_LINKS } = APP_CONSTANTS;

  const subMenuLinkProps = [
    // initialize with appropriate links based on feature flips and academy settings
    myTrainingsEnabled ? ACADEMY_SUB_MENU_LINKS.MY_TRAININGS : null,
    collectionsEnabled ? ACADEMY_SUB_MENU_LINKS.TRAININGS : null,
    pathsEnabled ? ACADEMY_SUB_MENU_LINKS.PATHS : null,
    certificationsEnabled ? ACADEMY_SUB_MENU_LINKS.CERTIFICATIONS : null,
    eventsEnabled ? ACADEMY_SUB_MENU_LINKS.EVENTS : null,
  ]
    .filter(Boolean)
    .map(({ LINK, KEY }) => {
      let badgeCount = null;
      if (KEY === ACADEMY_SUB_MENU_LINKS.MY_TRAININGS.KEY) {
        if (adminUser && adminUser.outstandingAssignmentsCount) {
          badgeCount = adminUser.outstandingAssignmentsCount;
        }
        if (currentUser && currentUser.outstandingAssignmentsCount) {
          badgeCount = currentUser.outstandingAssignmentsCount;
        }
      }

      const defaultLinkText = {
        my_trainings: I18n.t('common.academy.content.my_trainings'),
        trainings: I18n.t('common.academy.content.trainings'),
        paths: I18n.t('common.academy.content.paths'),
        certifications: I18n.t('common.academy.content.certifications'),
        events: I18n.t('common.academy.content.events'),
      };

      let linkText = themeOptions.tabTitles[KEY];
      linkText = linkText || defaultLinkText[KEY];

      return {
        id: KEY,
        link: LINK,
        linkText,
        badgeCount,
      };
    });

  const customPagesLinksProps = customPages.map(({ title, navigationTitle, slug, id }) => ({
    link: `/pages/${slug}`,
    linkText: navigationTitle || title,
    id,
  }));

  const allSubMenus = useMemo(
    () => [...subMenuLinkProps, ...customPagesLinksProps],
    [customPagesLinksProps, subMenuLinkProps],
  );
  const sortedSubMenus = useMemo(
    () => [...allSubMenus].sort((a, b) => pagesOrder.indexOf(a.id) - pagesOrder.indexOf(b.id)),
    [allSubMenus, pagesOrder],
  );

  return (
    <>
      {sortedSubMenus.length > 1 && (
        <AcademySubMenuWrapper
          className={academyCustomCSS.tabBar.bar}
          $subnavActiveLinkColor={subnavActiveLinkColor}
          $subnavAlign={subnavAlign}
          $subnavBackgroundColor={subnavBackgroundColor}
          $subnavLinkColor={subnavLinkColor}
        >
          <nav>
            <ul role="menubar">
              {sortedSubMenus.map(({ link, linkText, badgeCount }) => (
                <SubMenuLink
                  key={linkText}
                  link={link}
                  linkText={linkText}
                  isActive={currentMenu.LINK === link}
                  badgeCount={badgeCount}
                  subnavActiveLinkColor={subnavActiveLinkColor}
                />
              ))}
            </ul>
          </nav>
        </AcademySubMenuWrapper>
      )}
    </>
  );
}

const AcademySubMenuWrapper = styled.div`
  width: calc(100% - 8rem);
  padding: 0 4rem;
  ${media.mobile} {
    padding-left: 0;
    padding-right: 0;
    width: 100%;
  }
  background-color: ${(props) => (props.$subnavBackgroundColor ? props.$subnavBackgroundColor : '#F5F5F5')};
  nav {
    max-width: ${academyStyles.common.maxWidth}px;
    margin: 0 auto;
    text-align: left;
    ${(props) => props.$subnavAlign === 'center' && 'text-align: center;'}
    ${(props) => props.$subnavAlign === 'right' && 'text-align: right;'}
    ${media.mobile} {
      text-align: left;
    }
    ul {
      padding: 0;
      margin: 0;
      list-style: none;
      list-style-type: none;
      list-style-image: none;
      display: inline-block;
      ${media.mobile} {
        display: block;
      }
      li {
        display: inline-block;
        ${media.mobile} {
          display: block;
        }
        a {
          display: flex;
          align-items: center;
          font-weight: 600;
          border-bottom: solid 5px transparent;
          transition: all 0.3s ease-in-out;
          color: ${(props) => (props.$subnavLinkColor ? props.$subnavLinkColor : '#66778A')};
          padding: 20px 0;
          margin: 0 50px 0 0;
          ${(props) => props.$subnavAlign === 'center' && 'margin: 0 25px;'}
          ${(props) => props.$subnavAlign === 'right' && 'margin: 0 0 0 50px;'}
          ${media.mobile} {
            margin: 0 50px 0 0;
          }
          &.active,
          &:hover {
            color: ${(props) => (props.$subnavActiveLinkColor ? props.$subnavActiveLinkColor : '#1162BF')};
            border-bottom-color: ${(props) =>
              props.$subnavActiveLinkColor ? props.$subnavActiveLinkColor : '#1162BF'};
            text-decoration: none;
          }
          ${media.mobile} {
            margin: 0;
            padding: 15px 2rem;
            width: calc(100% - 4rem);
          }
        }
      }
    }
  }
`;
